import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Container, Eyebrow, useReveal } from "./ui";
import { colors, fonts, media, prefersReducedMotion } from "../../styles/theme";
import { devicesNew as devices } from "../../pages/landingContent";
import questMp4 from "../../vid/vimbal_quest_office.mp4";
import glassesMp4 from "../../vid/devices/glasses.mp4";
import glassesWebm from "../../vid/devices/glasses.webm";
import { posterFor, glassesStill } from "../../vid/posters";

gsap.registerPlugin(ScrollTrigger);

// "Where vimbal runs". Left: footage of a person in the
// headset customers actually receive, framed like the hero videos and run through the same
// warm filter. Right: vimbal's own glasses concept, same filter, captioned as ours. Meta's
// announcement sits beneath as a market note rather than as the caption for a render that
// is not Meta's product.

const Wrap = styled.section`
  padding: 0 0 140px;
`;

const Head = styled.div`
  margin-bottom: 26px;
`;

const Stage = styled.div`
  display: grid;
  gap: 18px;
  align-items: stretch;
  justify-items: center;

  @media ${media.small} {
    grid-template-columns: 1fr auto 1fr;
    gap: 28px;
  }
`;

const Device = styled.figure`
  width: 100%;
  max-width: 380px;
  text-align: center;
  align-self: start;
  /* The fade sits on the pieces, not the figure: an opacity on the figure would cut the
     turntable off from the page behind it, and its blend (below) needs the page. */
  --fade: ${(p) => p.$fade};
  .media > *,
  figcaption {
    opacity: var(--fade);
    transition: opacity 0.5s ease;
  }

  .media {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 230px;

    @media ${media.small} {
      height: 270px;
    }
  }
  /* the turntable: product on the page, no box, warmed to the palette. iPad Safari drew the
     clip's white matte instead of its transparency (30 Sep 2026), so the clip multiplies onto
     the page: white disappears on every browser. A soft oval under it replaces the drop shadow,
     which would have outlined the matte. */
  .media video.turn {
    position: relative;
    z-index: 1;
    display: block;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    mix-blend-mode: multiply;
    filter: saturate(0.85) sepia(0.08);
  }
  .media.spin::before {
    content: "";
    position: absolute;
    left: 16%;
    right: 16%;
    bottom: 12%;
    height: 34px;
    border-radius: 50%;
    background: radial-gradient(closest-side, rgba(42, 35, 30, 0.2), rgba(42, 35, 30, 0));
    opacity: var(--fade);
  }
  /* the still stands in until the clip actually plays (Low Power Mode blocks autoplay, and a
     paused clip on iPad showed its white matte) */
  .media video.turn.waiting {
    opacity: 0;
  }
  .media img.still {
    position: absolute;
    z-index: 1;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    mix-blend-mode: multiply;
    filter: saturate(0.85) sepia(0.08);
    pointer-events: none;
  }
  /* the footage: framed like the hero videos */
  .frame {
    width: 100%;
    height: 100%;
    border-radius: 24px;
    overflow: hidden;
    position: relative;
    background: ${colors.sand};
    box-shadow: 0 40px 80px -50px rgba(30, 42, 34, 0.6);
  }
  .frame video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: saturate(0.9) sepia(0.05);
  }
  figcaption {
    margin-top: 14px;
  }
  b {
    display: block;
    font-family: ${fonts.mono};
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${(p) => (p.$next ? colors.ocean : colors.moss)};
  }
  b small {
    margin-left: 6px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 9px;
    border: 1px solid currentColor;
  }
  span {
    display: block;
    margin-top: 6px;
    font-family: ${fonts.body};
    font-size: 15px;
    line-height: 1.45;
    color: ${colors.bark};
  }
`;

const Thread = styled.div`
  align-self: center;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${fonts.mono};
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${colors.copper};

  &::before,
  &::after {
    content: "";
    width: 46px;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${colors.copper});
  }
  &::after {
    background: linear-gradient(90deg, ${colors.copper}, transparent);
  }

  @media (max-width: 659px) {
    &::before,
    &::after {
      width: 28px;
    }
  }
`;

// Meta as context: one quiet line, set apart from the two captions.
const Market = styled.p`
  max-width: 640px;
  margin: 36px auto 0;
  text-align: center;
  font-family: ${fonts.body};
  font-size: 15px;
  line-height: 1.5;
  color: ${colors.pebble};
`;

const Note = styled.p`
  margin-top: 18px;
  text-align: center;
  font-family: ${fonts.mono};
  font-size: 9px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${colors.pebble};
`;

const useOnScreen = (ref) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => setOn(entry.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return on;
};

const Footage = ({ src, label }) => {
  const ref = useRef(null);
  const on = useOnScreen(ref);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (on) v.play().catch(() => {});
    else v.pause();
  }, [on]);
  return (
    <div className="frame">
      <video ref={ref} src={src} poster={posterFor(src)} muted loop playsInline preload="metadata" aria-label={label} />
    </div>
  );
};

const Turntable = ({ mp4, webm, still, label, active = true }) => {
  const ref = useRef(null);
  const on = useOnScreen(ref);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (on && active) v.play().catch(() => {});
    else v.pause();
  }, [on, active]);
  return (
    <>
      {!playing && <img className="still" src={still} alt="" aria-hidden="true" />}
      <video
        ref={ref}
        className={playing ? "turn" : "turn waiting"}
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={label}
        onPlaying={() => setPlaying(true)}
      >
        <source src={mp4} type="video/mp4; codecs=hvc1" />
        <source src={webm} type="video/webm" />
      </video>
    </>
  );
};

const DevicesNew = () => {
  const scope = useRef(null);
  const stage = useRef(null);
  const [handover, setHandover] = useState(0);
  const reduced = useMemo(() => prefersReducedMotion(), []);
  useReveal(scope);

  // The row of devices fades up like every other block, then drops the transform it was moved
  // with, so nothing isolates the turntable's blend from the page.
  useLayoutEffect(() => {
    const el = stage.current;
    if (!el || reduced || typeof IntersectionObserver === "undefined") return undefined;
    gsap.set(el, { opacity: 0, y: 40 });
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        gsap.to(el, { opacity: 1, y: 0, duration: 1.1, ease: "power3.out", delay: 0.1, clearProps: "transform,opacity" });
      },
      { rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      gsap.killTweensOf(el);
      gsap.set(el, { clearProps: "transform,opacity" });
    };
  }, [reduced]);

  useLayoutEffect(() => {
    if (reduced) {
      setHandover(1);
      return undefined;
    }
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: scope.current,
        start: "top 75%",
        end: "bottom 55%",
        scrub: 0.6,
        onUpdate: (self) => setHandover(Math.min(1, Math.max(0, (self.progress - 0.3) / 0.45))),
      });
    }, scope);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <Wrap ref={scope} aria-labelledby="devices-title">
      <Container>
        <Head data-reveal>
          <Eyebrow $color={colors.ocean}>{devices.eyebrow}</Eyebrow>
        </Head>
        <h2 id="devices-title" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
          {devices.title}
        </h2>
        <Stage ref={stage}>
          <Device $fade={1 - handover * 0.35}>
            <div className="media">
              <Footage src={questMp4} label={devices.today.alt} />
            </div>
            <figcaption>
              <b>
                {devices.today.label} <small>{devices.today.tag}</small>
              </b>
              <span>{devices.today.body}</span>
            </figcaption>
          </Device>
          <Thread aria-hidden="true">{devices.thread}</Thread>
          <Device $next $fade={0.45 + handover * 0.55}>
            <div className="media spin">
              <Turntable mp4={glassesMp4} webm={glassesWebm} still={glassesStill} label={devices.next.alt} active={handover > 0.05} />
            </div>
            <figcaption>
              <b>
                {devices.next.label} <small>{devices.next.tag}</small>
              </b>
              <span>{devices.next.body}</span>
            </figcaption>
          </Device>
        </Stage>
        <Market data-reveal="0.15">{devices.market}</Market>
        <Note>{devices.note}</Note>
      </Container>
    </Wrap>
  );
};

export default DevicesNew;
